import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import TaxonomySelect from '../../components/TaxonomySelect';
import { axiosInstance, Field, inputCls, labelCls } from './_shared';
import MapLocationPicker, { PHASE_DEFS } from './MapLocationPicker';
import PhasePDFUpload from './PhasePDFUpload';

// ─── Constants ────────────────────────────────────────────────────────────────
// Default empty event payload used by both the Create flow and the Edit flow
// (when an existing event has no `cricket_config` yet).
export const EMPTY_EVENT = {
  title: '', description: '', type: 'Play', sub_type: '', start_time: '', end_time: '',
  venue: { name: '', address: '', city: '', lat: '', lng: '' },
  ticket_tiers: [{ id: '', name: 'General Admission', price: 0, capacity: 100, sold: 0 }],
  image_url: '', max_tickets_per_user: 1,
  // Section 1: Event Basics
  organizer_name: '', organizer_contact: '', organizer_email: '',
  visibility: 'public', competition_type_value_id: '',
  // Section 3: Schedule
  match_duration_estimate: '',
  // Cricket config
  cricket_config: {
    format_value_id: '', category_value_id: '', ball_type_value_id: '',
    pitch_type_value_id: '', registration_mode_value_id: '',
    bowling_type_value_id: '',
    overs_per_innings: '', innings_per_match: '', min_players_to_start: '', powerplay_overs: '',
    wide_ball: null, no_ball: null, free_hit: null,
    tournament_structure_value_id: '', num_groups: '', knockout_stages: [],
    registration_type: 'free', prize_pool_json: { winner: '', runner_up: '', mvp: '', best_batter: '', best_bowler: '' },
    max_teams: '', max_players: '', squad_min: '', squad_max: '',
    playing_xi: '', substitutes_max: '',
    player_fee: '', team_owner_fee: '', sponsor_fee: '', team_fee: '',
    // Registrations the app takes — new tournaments start with players only
    registration_roles: ['player'],
  },
};

// Registrations the app can take, each with its own fee field (paise)
const ROLE_ROWS = [
  { role: 'player', label: 'Player registrations', fee: 'player_fee', testId: 'cricket-player-fee' },
  { role: 'team_owner', label: 'Team owner registrations', fee: 'team_owner_fee', testId: 'cricket-team-owner-fee',
    note: 'Needed for an auction — owners bid for players.' },
  { role: 'sponsor', label: 'Sponsor registrations', fee: 'sponsor_fee', testId: 'cricket-sponsor-fee' },
];

// Default phases for tournament events.
export const DEFAULT_PHASES = [
  { phase_key: 'overview', is_enabled: true,  title: '', description: '', rules_pdf_url: null, start_date: null, end_date: null },
  { phase_key: 'register', is_enabled: true,  title: '', description: '', rules_pdf_url: null, start_date: null, end_date: null },
  { phase_key: 'teams',    is_enabled: true,  title: '', description: '', rules_pdf_url: null, start_date: null, end_date: null },
  { phase_key: 'auction',  is_enabled: false, title: '', description: '', rules_pdf_url: null, start_date: null, end_date: null },
  { phase_key: 'fixtures', is_enabled: false, title: '', description: '', rules_pdf_url: null, start_date: null, end_date: null },
  { phase_key: 'live',     is_enabled: false, title: '', description: '', rules_pdf_url: null, start_date: null, end_date: null },
  { phase_key: 'results',  is_enabled: false, title: '', description: '', rules_pdf_url: null, start_date: null, end_date: null },
];

// Sub-types under the "Play" event type — extend here for future sports.
export const PLAY_SUB_TYPES = ['Cricket'];

export default function EventForm({ initial, onSave, onCancel, isCricketEdit, canPropose = false }) {
  const [form, setForm] = useState(() => {
    const base = initial || EMPTY_EVENT;
    if (!base.cricket_config) {
      base.cricket_config = { ...EMPTY_EVENT.cricket_config };
    }
    if (!base.sub_type) base.sub_type = '';
    return base;
  });
  const [loading, setLoading] = useState(false);
  const [taxonomyValues, setTaxonomyValues] = useState({});
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [phases, setPhases] = useState(DEFAULT_PHASES.map(p => ({ ...p })));
  const [expandedPhase, setExpandedPhase] = useState(null);

  const isCricket = form.type === 'Play' && form.sub_type === 'Cricket';

  // Sync Overview phase dates with event start/end times
  useEffect(() => {
    if (isCricket && form.start_time) {
      setPhases(prev => prev.map(p => 
        p.phase_key === 'overview' 
          ? { ...p, start_date: form.start_time, end_date: form.end_time || form.start_time }
          : p
      ));
    }
  }, [form.start_time, form.end_time, isCricket]);

  const updatePhase = (phaseKey, field, value) => {
    setPhases(prev => prev.map(p => 
      p.phase_key === phaseKey ? { ...p, [field]: value } : p
    ));
  };

  const togglePhase = (phaseKey) => {
    setPhases(prev => prev.map(p => 
      p.phase_key === phaseKey ? { ...p, is_enabled: !p.is_enabled } : p
    ));
  };

  // Load taxonomy values when any sport is selected (competition_type is sport-agnostic)
  const isSportSelected = form.type === 'Play' && !!form.sub_type;
  useEffect(() => {
    if (!isSportSelected) return;
    let cancelled = false;
    const loadTaxonomy = async () => {
      setTaxonomyLoading(true);
      try {
        const typesRes = await axiosInstance.get('/v1/taxonomy/types');
        const valMap = {};
        for (const t of typesRes.data) {
          const vRes = await axiosInstance.get(`/v1/taxonomy/${t.code}/values?status=active`);
          valMap[t.code] = vRes.data;
        }
        if (!cancelled) setTaxonomyValues(valMap);
      } catch {
        toast.error('Failed to load taxonomy data');
      } finally {
        if (!cancelled) setTaxonomyLoading(false);
      }
    };
    loadTaxonomy();
    return () => { cancelled = true; };
  }, [isSportSelected]);

  const set = (path, val) => setForm(f => {
    const clone = JSON.parse(JSON.stringify(f));
    const keys = path.split('.');
    let obj = clone;
    for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
    obj[keys[keys.length - 1]] = val;
    return clone;
  });

  const handleTier = (i, key, val) => {
    const tiers = [...form.ticket_tiers];
    tiers[i] = { ...tiers[i], [key]: key === 'price' || key === 'capacity' ? Number(val) : val };
    setForm(f => ({ ...f, ticket_tiers: tiers }));
  };
  const addTier = () => setForm(f => ({ ...f, ticket_tiers: [...f.ticket_tiers, { id: '', name: '', price: 0, capacity: 50, sold: 0 }] }));
  const removeTier = i => setForm(f => ({ ...f, ticket_tiers: f.ticket_tiers.filter((_, idx) => idx !== i) }));

  // Upload event image to Cloudinary
  const uploadEventImage = async (file) => {
    if (!file) return;
    
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setImageUploading(true);
    try {
      // Get signature for upload
      const sigRes = await axiosInstance.get('/cloudinary/signature?resource_type=image&folder=events/images');
      const sig = sigRes.data;

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );

      if (!uploadRes.ok) {
        throw new Error('Upload failed');
      }

      const result = await uploadRes.json();
      set('image_url', result.secure_url);
      toast.success('Image uploaded successfully!');
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const selectedRegMode = taxonomyValues?.registration_mode?.find(v => v.id === form.cricket_config?.registration_mode_value_id)?.key;

  const save = async () => {
    if (!form.title || !form.start_time || !form.end_time || !form.venue.name) { toast.error('Fill all required fields'); return; }
    setLoading(true);
    try {
      if (isCricket) {
        // Build cricket config, filter out empty values
        const config = {};
        ['format_value_id', 'category_value_id', 'ball_type_value_id', 'pitch_type_value_id',
         'registration_mode_value_id', 'bowling_type_value_id', 'tournament_structure_value_id'].forEach(k => {
          if (form.cricket_config[k]) config[k] = form.cricket_config[k];
        });
        ['max_teams', 'max_players', 'squad_min', 'squad_max', 'playing_xi', 'substitutes_max',
         'player_fee', 'team_owner_fee', 'sponsor_fee', 'team_fee',
         'overs_per_innings', 'innings_per_match', 'min_players_to_start', 'powerplay_overs',
         'num_groups'].forEach(k => {
          if (form.cricket_config[k] !== '' && form.cricket_config[k] !== null && form.cricket_config[k] !== undefined)
            config[k] = parseInt(form.cricket_config[k], 10);
        });
        if (form.cricket_config.wide_ball   !== null) config.wide_ball   = form.cricket_config.wide_ball;
        if (form.cricket_config.no_ball     !== null) config.no_ball     = form.cricket_config.no_ball;
        if (form.cricket_config.free_hit    !== null) config.free_hit    = form.cricket_config.free_hit;
        if (form.cricket_config.knockout_stages?.length) config.knockout_stages = form.cricket_config.knockout_stages;
        // Section 7 — which registrations the app takes (fee 0 = free)
        const roles = form.cricket_config.registration_roles || ['player'];
        config.registration_roles = roles;
        config.registration_type = roles.some((r) => parseInt(form.cricket_config[ROLE_ROWS.find((x) => x.role === r).fee], 10) > 0)
          ? 'paid' : 'free';
        // Always include prize_pool_json even if values are 0, but clean up empty strings
        if (form.cricket_config.prize_pool_json) {
          const cleanPool = {};
          Object.entries(form.cricket_config.prize_pool_json).forEach(([k, v]) => {
            cleanPool[k] = (v === '' || v === null || v === undefined) ? 0 : Number(v);
          });
          config.prize_pool_json = cleanPool;
        }

        if (isCricketEdit) {
          // PATCH existing cricket event
          await onSave({
            _cricket: true,
            title: form.title,
            description: form.description,
            starts_at: new Date(form.start_time).toISOString(),
            ends_at: new Date(form.end_time).toISOString(),
            venue: { name: form.venue.name, city: form.venue.city, address: form.venue.address || '' },
            image_url: form.image_url,
            config,
            organizer_name: form.organizer_name || null,
            organizer_contact: form.organizer_contact || null,
            organizer_email: form.organizer_email || null,
            visibility: form.visibility || 'public',
            competition_type_value_id: form.competition_type_value_id || null,
            match_duration_estimate: form.match_duration_estimate || null,
          });
        } else {
          // POST new cricket event via v1 API with phases
          await axiosInstance.post('/v1/admin/events/cricket', {
            title: form.title,
            description: form.description,
            starts_at: new Date(form.start_time).toISOString(),
            ends_at: new Date(form.end_time).toISOString(),
            venue: { name: form.venue.name, city: form.venue.city, address: form.venue.address || '' },
            image_url: form.image_url,
            config,
            organizer_name: form.organizer_name || null,
            organizer_contact: form.organizer_contact || null,
            organizer_email: form.organizer_email || null,
            visibility: form.visibility || 'public',
            competition_type_value_id: form.competition_type_value_id || null,
            match_duration_estimate: form.match_duration_estimate || null,
            phases: phases.map(p => ({
              phase_key: p.phase_key,
              is_enabled: p.is_enabled,
              start_date: p.start_date || null,
              end_date: p.end_date || null,
              title: p.title || null,
              description: p.description || null,
              rules_pdf_url: p.rules_pdf_url || null,
            })),
          });
          toast.success('Cricket event created (draft)');
          onSave(null); // signal success without payload
        }
      } else {
        // Standard event creation
        const sanitized = {
          ...form,
          venue: {
            ...form.venue,
            lat: form.venue.lat !== '' && form.venue.lat !== undefined ? parseFloat(form.venue.lat) || null : null,
            lng: form.venue.lng !== '' && form.venue.lng !== undefined ? parseFloat(form.venue.lng) || null : null,
          },
          ticket_tiers: form.ticket_tiers.map(t => ({
            ...t,
            price: Number(t.price) || 0,
            capacity: Number(t.capacity) || 0,
          })),
          max_tickets_per_user: Number(form.max_tickets_per_user) || 1,
        };
        delete sanitized.sub_type;
        delete sanitized.cricket_config;
        await onSave(sanitized);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail.errors) {
        detail.errors.forEach(err => toast.error(`${err.field}: ${err.message}`));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to save event');
      }
    }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4 overflow-y-auto max-h-[75vh] pr-1" data-testid="event-form">
      <Field label="Title" required><input className={inputCls} value={form.title} onChange={e => set('title', e.target.value)} data-testid="event-title-input" /></Field>
      <Field label="Description"><textarea className={inputCls} rows={3} value={form.description} onChange={e => set('description', e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type" required>
          <select className={inputCls} value={form.type} onChange={e => { set('type', e.target.value); if (e.target.value !== 'Play') set('sub_type', ''); }} data-testid="event-type-select">
            <option>Play</option><option>Move</option><option>Mind</option>
          </select>
        </Field>
        {form.type === 'Play' ? (
          <Field label="Sport / Sub-type">
            <select className={inputCls} value={form.sub_type} onChange={e => set('sub_type', e.target.value)} data-testid="event-subtype-select">
              <option value="">General</option>
              {PLAY_SUB_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        ) : (
          <Field label="Max Tickets/User"><input type="number" className={inputCls} value={form.max_tickets_per_user} onChange={e => set('max_tickets_per_user', Number(e.target.value))} /></Field>
        )}
      </div>

      {/* Section 1: Competition Type - shown whenever a sport is selected under Play */}
      {form.type === 'Play' && form.sub_type && (
        <Field label="Competition Type">
          <TaxonomySelect
            typeCode="competition_type"
            options={taxonomyValues?.competition_type || []}
            value={form.competition_type_value_id || ''}
            onChange={v => set('competition_type_value_id', v)}
            placeholder="Select competition type…"
            className={inputCls}
            testId="competition-type-select"
            canPropose={canPropose}
          />
        </Field>
      )}

      {/* Section 1: Organizer Details */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Organizer Details</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Organizer Name" required>
            <input
              className={inputCls}
              value={form.organizer_name || ''}
              onChange={e => set('organizer_name', e.target.value)}
              placeholder="e.g. Backyard Sports Club"
              data-testid="organizer-name-input"
            />
          </Field>
          <Field label="Organizer Contact" required>
            <input
              className={inputCls}
              value={form.organizer_contact || ''}
              onChange={e => set('organizer_contact', e.target.value)}
              placeholder="e.g. +91 98765 43210"
              data-testid="organizer-contact-input"
            />
          </Field>
          <Field label="Organizer Email">
            <input
              type="email"
              className={inputCls}
              value={form.organizer_email || ''}
              onChange={e => set('organizer_email', e.target.value)}
              placeholder="e.g. organizer@club.com"
              data-testid="organizer-email-input"
            />
          </Field>
          <Field label="Visibility">
            <select
              className={inputCls}
              value={form.visibility || 'public'}
              onChange={e => set('visibility', e.target.value)}
              data-testid="visibility-select"
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="invite_only">Invite Only</option>
            </select>
          </Field>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Start Date" required><input type="datetime-local" className={inputCls} value={form.start_time ? form.start_time.slice(0, 16) : ''} onChange={e => set('start_time', e.target.value)} data-testid="event-start-time" /></Field>
        <Field label="End Date" required><input type="datetime-local" className={inputCls} value={form.end_time ? form.end_time.slice(0, 16) : ''} onChange={e => set('end_time', e.target.value)} data-testid="event-end-time" /></Field>
      </div>
      <Field label="Match Duration Estimate">
        <input
          className={inputCls}
          value={form.match_duration_estimate || ''}
          onChange={e => set('match_duration_estimate', e.target.value)}
          placeholder="e.g. 2 hours per match"
          data-testid="match-duration-input"
        />
      </Field>
      <Field label="Image URL"><input className={inputCls} value={form.image_url} onChange={e => set('image_url', e.target.value)} placeholder="https://…" /></Field>
      
      {/* Event Image Upload */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Event Banner Image</p>
        <div className="flex items-start gap-4">
          {/* Image Preview */}
          <div className="w-32 h-20 rounded-xl bg-gray-200 overflow-hidden flex items-center justify-center border-2 border-dashed border-gray-300 shrink-0">
            {form.image_url ? (
              <img src={form.image_url} alt="Event banner" className="w-full h-full object-cover" />
            ) : (
              <i className="ph-bold ph-image text-2xl text-gray-400"></i>
            )}
          </div>
          
          {/* Upload Controls */}
          <div className="flex-1 space-y-2">
            <label className="relative cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-[#2B3A67] text-white font-bold text-sm rounded-xl hover:bg-[#1e2a4a] transition-colors">
              <i className={`ph-bold ${imageUploading ? 'ph-spinner animate-spin' : 'ph-upload-simple'}`}></i>
              {imageUploading ? 'Uploading...' : 'Upload Image'}
              <input
                type="file"
                accept="image/*"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={e => e.target.files[0] && uploadEventImage(e.target.files[0])}
                disabled={imageUploading}
                data-testid="event-image-upload"
              />
            </label>
            <p className="text-xs text-gray-400">PNG, JPG up to 5MB. Recommended: 1200×630px</p>
            {form.image_url && (
              <button 
                type="button" 
                onClick={() => set('image_url', '')} 
                className="text-xs text-red-500 hover:text-red-700 font-semibold"
                data-testid="event-image-remove"
              >
                <i className="ph-bold ph-trash mr-1"></i>Remove Image
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Venue</p>
        {/* Map location picker - above form fields */}
        <div>
          <label className={`${labelCls} mb-1.5`}>Pin Location on Map</label>
          <MapLocationPicker
            venue={form.venue}
            onVenueChange={updatedVenue => setForm(f => ({ ...f, venue: updatedVenue }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Venue Name" required>
            <input className={inputCls} value={form.venue.name} onChange={e => set('venue.name', e.target.value)} placeholder="e.g. DY Patil Stadium" />
          </Field>
          <Field label="City">
            <input className={inputCls} value={form.venue.city} onChange={e => set('venue.city', e.target.value)} placeholder="e.g. Mumbai" />
          </Field>
          <div className="col-span-2">
            <Field label="Address">
              <input className={inputCls} value={form.venue.address} onChange={e => set('venue.address', e.target.value)} placeholder="Full address" />
            </Field>
          </div>
          <Field label="Lighting">
            <select
              className={inputCls}
              value={form.venue.lighting || ''}
              onChange={e => set('venue.lighting', e.target.value)}
              data-testid="venue-lighting-select"
            >
              <option value="">Select…</option>
              <option value="day">Day</option>
              <option value="floodlights">Floodlights</option>
              <option value="both">Both</option>
            </select>
          </Field>
        </div>
      </div>

      {/* Cricket Configuration Section */}
      {isCricket && (
        <div className="bg-blue-50/60 rounded-xl p-4 space-y-4 border border-blue-100" data-testid="cricket-config-section">
          <p className="text-xs font-bold text-[#2B3A67] uppercase tracking-wider flex items-center gap-1.5">
            <i className="ph-bold ph-baseball text-sm" /> Cricket Configuration
          </p>
          {taxonomyLoading ? (
            <div className="text-xs text-gray-400 py-3 text-center">Loading configuration options...</div>
          ) : (
            <>
              {/* --- General Config --- */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'format_value_id', code: 'cricket_format', label: 'Format' },
                  { key: 'category_value_id', code: 'age_category', label: 'Age Category' },
                  { key: 'pitch_type_value_id', code: 'pitch_type', label: 'Pitch Type' },
                  { key: 'registration_mode_value_id', code: 'registration_mode', label: 'Registration Mode' },
                ].map(({ key, code, label }) => (
                  <Field key={key} label={label}>
                    <TaxonomySelect
                      typeCode={code}
                      options={taxonomyValues[code] || []}
                      value={form.cricket_config?.[key] || ''}
                      onChange={v => set(`cricket_config.${key}`, v)}
                      className={inputCls}
                      testId={`cricket-${code}-select`}
                      canPropose={canPropose}
                    />
                  </Field>
                ))}
              </div>

              {/* --- Section 4: Match Format --- */}
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-1">Match Format</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ball Type">
                  <TaxonomySelect
                    typeCode="ball_type"
                    options={taxonomyValues['ball_type'] || []}
                    value={form.cricket_config?.ball_type_value_id || ''}
                    onChange={v => set('cricket_config.ball_type_value_id', v)}
                    className={inputCls}
                    testId="cricket-ball_type-select"
                    canPropose={canPropose}
                  />
                </Field>
                <Field label="Bowling Type">
                  <TaxonomySelect
                    typeCode="bowling_type"
                    options={taxonomyValues['bowling_type'] || []}
                    value={form.cricket_config?.bowling_type_value_id || ''}
                    onChange={v => set('cricket_config.bowling_type_value_id', v)}
                    className={inputCls}
                    testId="cricket-bowling_type-select"
                    canPropose={canPropose}
                  />
                </Field>
              </div>

              {/* --- Section 4: Overs --- */}
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-1">Overs</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Overs Per Innings">
                  <input type="number" min="1" max="50" className={inputCls} value={form.cricket_config?.overs_per_innings || ''} onChange={e => set('cricket_config.overs_per_innings', e.target.value)} placeholder="e.g. 20" data-testid="cricket-overs-per-innings" />
                </Field>
                <Field label="Innings Per Match">
                  <select className={inputCls} value={form.cricket_config?.innings_per_match || ''} onChange={e => set('cricket_config.innings_per_match', e.target.value)} data-testid="cricket-innings-per-match">
                    <option value="">Select...</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                  </select>
                </Field>
              </div>

              {/* --- Section 4: Player Format --- */}
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-1">Player Format</p>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Players Per Team">
                  <input type="number" min="1" className={inputCls} value={form.cricket_config?.playing_xi || ''} onChange={e => set('cricket_config.playing_xi', e.target.value)} placeholder="e.g. 11" data-testid="cricket-playing-xi" />
                </Field>
                <Field label="Max Squad Size">
                  <input type="number" min="1" className={inputCls} value={form.cricket_config?.squad_max || ''} onChange={e => set('cricket_config.squad_max', e.target.value)} placeholder="e.g. 15" data-testid="cricket-squad-max" />
                </Field>
                <Field label="Min Players to Start">
                  <input type="number" min="1" className={inputCls} value={form.cricket_config?.min_players_to_start || ''} onChange={e => set('cricket_config.min_players_to_start', e.target.value)} placeholder="e.g. 7" data-testid="cricket-min-players-to-start" />
                </Field>
              </div>

              {/* --- Section 4: Match Rules --- */}
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-1">Match Rules</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'wide_ball', label: 'Wide Ball Rule', testId: 'cricket-wide-ball' },
                  { key: 'no_ball',   label: 'No Ball Rule',   testId: 'cricket-no-ball' },
                  { key: 'free_hit',  label: 'Free Hit Rule',  testId: 'cricket-free-hit' },
                ].map(({ key, label, testId }) => (
                  <Field key={key} label={label}>
                    <select className={inputCls} value={form.cricket_config?.[key] === true ? 'true' : form.cricket_config?.[key] === false ? 'false' : ''} onChange={e => set(`cricket_config.${key}`, e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)} data-testid={testId}>
                      <option value="">Not Set</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </Field>
                ))}
                <Field label="Powerplay Overs">
                  <input type="number" min="0" max="50" className={inputCls} value={form.cricket_config?.powerplay_overs || ''} onChange={e => set('cricket_config.powerplay_overs', e.target.value)} placeholder="e.g. 6" data-testid="cricket-powerplay-overs" />
                </Field>
              </div>

              {/* --- Section 5: Tournament Structure --- */}
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-1">Tournament Structure</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Tournament Structure">
                  <TaxonomySelect
                    typeCode="tournament_structure"
                    options={taxonomyValues['tournament_structure'] || []}
                    value={form.cricket_config?.tournament_structure_value_id || ''}
                    onChange={v => set('cricket_config.tournament_structure_value_id', v)}
                    className={inputCls}
                    testId="cricket-tournament-structure-select"
                    canPropose={canPropose}
                  />
                </Field>
                {/* Number of Groups — only shown when Groups + Playoffs is selected */}
                {(taxonomyValues['tournament_structure'] || []).find(v => v.id === form.cricket_config?.tournament_structure_value_id)?.key === 'groups_playoffs' && (
                  <Field label="Number of Groups">
                    <input
                      type="number" min="1" max="10"
                      className={inputCls}
                      value={form.cricket_config?.num_groups || ''}
                      onChange={e => set('cricket_config.num_groups', e.target.value)}
                      placeholder="e.g. 4"
                      data-testid="cricket-num-groups"
                    />
                  </Field>
                )}
              </div>
              {/* Knockout Stages multi-select */}
              <Field label="Knockout Stages">
                <div className="flex gap-4 flex-wrap pt-1">
                  {[
                    { key: 'quarter_final', label: 'Quarter Final' },
                    { key: 'semi_final',    label: 'Semi Final' },
                    { key: 'final',         label: 'Final' },
                  ].map(({ key, label }) => {
                    const stages = form.cricket_config?.knockout_stages || [];
                    const checked = stages.includes(key);
                    return (
                      <label key={key} className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid={`cricket-knockout-${key}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked ? stages.filter(s => s !== key) : [...stages, key];
                            set('cricket_config.knockout_stages', next);
                          }}
                          className="rounded border-gray-300 text-[#2B3A67] focus:ring-[#2B3A67]"
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </Field>

              {/* --- Capacity (conditional on reg mode) --- */}
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-1">Capacity</p>
              <div className="grid grid-cols-3 gap-3">
                {selectedRegMode === 'team' && (
                  <>
                    <Field label="Max Teams"><input type="number" className={inputCls} value={form.cricket_config?.max_teams || ''} onChange={e => set('cricket_config.max_teams', e.target.value)} data-testid="cricket-max-teams" /></Field>
                    <Field label="Squad Min"><input type="number" className={inputCls} value={form.cricket_config?.squad_min || ''} onChange={e => set('cricket_config.squad_min', e.target.value)} /></Field>
                    <Field label="Subs Max"><input type="number" className={inputCls} value={form.cricket_config?.substitutes_max || ''} onChange={e => set('cricket_config.substitutes_max', e.target.value)} /></Field>
                  </>
                )}
                {selectedRegMode === 'individual' && (
                  <Field label="Max Players"><input type="number" className={inputCls} value={form.cricket_config?.max_players || ''} onChange={e => set('cricket_config.max_players', e.target.value)} data-testid="cricket-max-players" /></Field>
                )}
                {!selectedRegMode && (
                  <>
                    <Field label="Max Teams"><input type="number" className={inputCls} value={form.cricket_config?.max_teams || ''} onChange={e => set('cricket_config.max_teams', e.target.value)} /></Field>
                    <Field label="Max Players"><input type="number" className={inputCls} value={form.cricket_config?.max_players || ''} onChange={e => set('cricket_config.max_players', e.target.value)} /></Field>
                  </>
                )}
              </div>
            </>
          )}

          {/* Section 7: Fees & Prize Pool */}
          <>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-2">Fees & Registration</p>

            {/* Which registrations the app takes, each with its own fee */}
            <div className="space-y-2" data-testid="registration-roles">
              <p className="text-xs text-gray-500">Tick the registrations you'll take in the app — only these are shown to visitors. Leave a fee at 0 for free.</p>
              {ROLE_ROWS.map(({ role, label, fee, note, testId }) => {
                const roles = form.cricket_config?.registration_roles || [];
                const on = roles.includes(role);
                return (
                  <div key={role} className={`flex items-center gap-3 p-3 rounded-xl border-2 ${on ? 'border-[#2B3A67]/40 bg-white' : 'border-gray-100 bg-gray-50'}`}>
                    <label className="flex items-start gap-2 flex-1 min-w-0 cursor-pointer select-none">
                      <input type="checkbox" checked={on} className="mt-1"
                        onChange={() => set('cricket_config.registration_roles',
                          on ? roles.filter((r) => r !== role) : [...roles, role])}
                        data-testid={`accept-${role}`} />
                      <span className="min-w-0">
                        <span className="font-bold text-sm block">{label}</span>
                        {note && <span className="text-xs text-gray-400">{note}</span>}
                      </span>
                    </label>
                    <div className="w-28 shrink-0">
                      <input type="number" min="0" disabled={!on}
                        className={`${inputCls} disabled:opacity-40`}
                        value={form.cricket_config?.[fee] ? Math.round(form.cricket_config[fee] / 100) : ''}
                        onChange={(e) => set(`cricket_config.${fee}`, e.target.value ? parseInt(e.target.value, 10) * 100 : 0)}
                        placeholder="₹ 0" aria-label={`${label} fee (₹)`} data-testid={testId} />
                    </div>
                  </div>
                );
              })}
              {!(form.cricket_config?.registration_roles || []).length && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">No registrations ticked — visitors won't be able to register in the app.</p>
              )}
              <Field label="Per Team Fee (₹)">
                <input
                  type="number" min="0"
                  className={inputCls}
                  value={form.cricket_config?.team_fee ? Math.round(form.cricket_config.team_fee / 100) : ''}
                  onChange={e => set('cricket_config.team_fee', e.target.value ? parseInt(e.target.value) * 100 : 0)}
                  placeholder="0"
                  data-testid="cricket-team-fee"
                />
              </Field>
              <p className="text-xs text-gray-400">The team fee applies only when teams register themselves (Teams → Team registration) and choose to pay once for the whole team.</p>
            </div>

            {/* Prize Pool */}
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-1">Prize Pool (₹ INR)</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'winner',      label: 'Winner',      testId: 'cricket-prize-winner' },
                { key: 'runner_up',   label: 'Runner Up',   testId: 'cricket-prize-runner-up' },
                { key: 'mvp',         label: 'MVP',         testId: 'cricket-prize-mvp' },
                { key: 'best_batter', label: 'Best Batter', testId: 'cricket-prize-best-batter' },
                { key: 'best_bowler', label: 'Best Bowler', testId: 'cricket-prize-best-bowler' },
              ].map(({ key, label, testId }) => (
                <Field key={key} label={label}>
                  <input
                    type="number" min="0"
                    className={inputCls}
                    value={form.cricket_config?.prize_pool_json?.[key] ? Math.round(form.cricket_config.prize_pool_json[key] / 100) : ''}
                    onChange={e => {
                      const pool = { ...(form.cricket_config?.prize_pool_json || {}), [key]: e.target.value ? parseInt(e.target.value) * 100 : 0 };
                      set('cricket_config.prize_pool_json', pool);
                    }}
                    placeholder="0"
                    data-testid={testId}
                  />
                </Field>
              ))}
            </div>
          </>
        </div>
      )}

      {/* Ticket Tiers - only for non-cricket events */}
      {!isCricket && (
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Ticket Tiers</p>
            <button onClick={addTier} className="text-xs font-bold text-[#2B3A67]">+ Add Tier</button>
          </div>
          {form.ticket_tiers.map((tier, i) => (
            <div key={i} className="bg-white rounded-xl p-3 space-y-2 border border-gray-100">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-gray-500">Tier {i + 1}</p>
                {form.ticket_tiers.length > 1 && <button onClick={() => removeTier(i)} className="text-red-400 text-xs">Remove</button>}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Name"><input className={inputCls} value={tier.name} onChange={e => handleTier(i, 'name', e.target.value)} /></Field>
                <Field label="Price"><input type="number" className={inputCls} value={tier.price} onChange={e => handleTier(i, 'price', e.target.value)} /></Field>
                <Field label="Capacity"><input type="number" className={inputCls} value={tier.capacity} onChange={e => handleTier(i, 'capacity', e.target.value)} /></Field>
              </div>
            </div>
          ))}
        </div>
      )}

      {isCricket && form.type === 'Play' && !isCricketEdit && (
        <p className="text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-2.5">
          Cricket events are created in <span className="font-bold">draft</span> status. Use the publish workflow to go live.
        </p>
      )}

      {/* Tournament Phases Configuration - Only for new cricket events */}
      {isCricket && !isCricketEdit && (
        <div className="bg-gray-50 rounded-xl p-4 space-y-3" data-testid="phases-config-section">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-2">
              <i className="ph-bold ph-list-checks text-[#3AAFA9]"></i>
              Tournament Phases
            </p>
            <span className="text-xs text-gray-400">
              {phases.filter(p => p.is_enabled).length} of {phases.length} enabled
            </span>
          </div>
          
          <div className="space-y-2">
            {phases.map((phase) => {
              const def = PHASE_DEFS[phase.phase_key] || { title: phase.phase_key, icon: 'ph-circle', description: '' };
              const isExpanded = expandedPhase === phase.phase_key;
              
              return (
                <div key={phase.phase_key}
                  className={`border rounded-xl overflow-hidden transition-all bg-white ${
                    phase.is_enabled ? 'border-[#3AAFA9]' : 'border-gray-200'
                  }`}>
                  {/* Phase Header */}
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => togglePhase(phase.phase_key)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                          phase.is_enabled ? 'bg-[#3AAFA9] text-white' : 'bg-gray-100 text-gray-400'
                        }`}>
                        <i className={`ph-bold ${def.icon} text-sm`}></i>
                      </button>
                      <div>
                        <p className="font-bold text-xs">{def.title}</p>
                        <p className="text-[10px] text-gray-400">{def.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={phase.is_enabled} onChange={() => togglePhase(phase.phase_key)}
                          className="w-3.5 h-3.5 rounded text-[#3AAFA9] focus:ring-[#3AAFA9]" />
                        <span className="text-[10px] font-bold text-gray-400">On</span>
                      </label>
                      <button type="button" onClick={() => setExpandedPhase(isExpanded ? null : phase.phase_key)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                        <i className={`ph-bold ${isExpanded ? 'ph-caret-up' : 'ph-caret-down'} text-gray-400 text-sm`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Expanded Config */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 p-3 space-y-3 bg-gray-50">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 mb-1">Start Date</label>
                          <input type="datetime-local"
                            value={(() => {
                              if (!phase.start_date) return '';
                              const d = new Date(phase.start_date);
                              return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                            })()}
                            onChange={(e) => updatePhase(phase.phase_key, 'start_date', e.target.value ? new Date(e.target.value).toISOString() : null)}
                            className={inputCls + ' !py-2 !text-xs'} />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 mb-1">End Date</label>
                          <input type="datetime-local"
                            value={(() => {
                              if (!phase.end_date) return '';
                              const d = new Date(phase.end_date);
                              return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                            })()}
                            onChange={(e) => updatePhase(phase.phase_key, 'end_date', e.target.value ? new Date(e.target.value).toISOString() : null)}
                            className={inputCls + ' !py-2 !text-xs'} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 mb-1">
                          {phase.phase_key === 'overview' ? 'Event Rules PDF (Generic)' : 'Phase Rules PDF'}
                        </label>
                        <PhasePDFUpload
                          phaseKey={phase.phase_key}
                          currentUrl={phase.rules_pdf_url}
                          onUploaded={(url) => updatePhase(phase.phase_key, 'rules_pdf_url', url)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-gray-400 text-center">
            Enable phases to show on tournament page. Set start dates for "Coming Soon" display.
          </p>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button onClick={onCancel} className="flex-1 py-3 rounded-2xl border-2 border-gray-200 font-bold text-sm">Cancel</button>
        <button onClick={save} disabled={loading} className="flex-1 py-3 rounded-2xl bg-[#2B3A67] text-white font-extrabold text-sm disabled:opacity-60" data-testid="save-event-btn">
          {loading ? 'Saving…' : isCricketEdit ? 'Save Changes' : isCricket ? 'Create Draft' : 'Save Event'}
        </button>
      </div>
    </div>
  );
}
